import "./AppLayout.css";

import { useEffect, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

import { BrandColors } from "../../brandColors";
import { TOURNAMENT_ID, MATCH_ID } from "../../config/tournament";
import { useDefaultMatch, useMediaQuery } from "../../hooks";
import { useInit } from "../../hooks/useInit";
import { setDayjsLocale } from "../../i18n/dayjsConfig";
import { SupportedLngsType } from "../../i18n/helper";
import useLocalizeDocumentAttributes from "../../i18n/useLocalizeDocumentAttributes";
import { useAppStore, useFixtureScore } from "../../stores";
import { getPersistedMatchId, persistMatch } from "../../utils/matchPersistence";
import { SoccerMatchStatus } from "../../../../types";
import { DataLoader } from "../DataLoader";
import { ErrorScreen } from "../ErrorScreen";
import { ScheduleCarousel } from "../ScheduleCarousel";

const rootStyles = `
  :root {
    --font-primary: "Roboto";
    --font-secondary: "Ubuntu";
    --font-tertiary: "GT America";
    --background-primary: ${BrandColors.BACKGROUND_PRIMARY};
    --background-secondary: ${BrandColors.BACKGROUND_SECONDARY};
    --background-ternary: ${BrandColors.BACKGROUND_TERNARY};
    --background-red: ${BrandColors.BACKGROUND_RED};
    --background-flag-loader: ${BrandColors.BACKGROUND_FLAG_LOADER};
    --border-primary: ${BrandColors.BORDER_PRIMARY};
    --border-secondary: ${BrandColors.BORDER_SECONDARY};
    --border-team-qualified: ${BrandColors.BORDER_TEAM_QUALIFIED};
    --text-primary: ${BrandColors.TEXT_PRIMARY};
    --text-secondary: ${BrandColors.TEXT_SECONDARY};
    --text-ternary: ${BrandColors.TEXT_TERNARY};
    --white: ${BrandColors.WHITE};
    --black: ${BrandColors.BLACK};

    --surface-option: ${BrandColors.SURFACE_OPTION};
    --surface-alt: ${BrandColors.SURFACE_ALT};
    --surface-muted: ${BrandColors.SURFACE_MUTED};
    --surface-light: ${BrandColors.SURFACE_LIGHT};

    --text-title: ${BrandColors.TEXT_TITLE};
    --dark-900: ${BrandColors.DARK_900};
    --dark-800: ${BrandColors.DARK_800};
    --dark-700: ${BrandColors.DARK_700};
    --dark-600: ${BrandColors.DARK_600};
    --dark-500: ${BrandColors.DARK_500};

    --green: ${BrandColors.GREEN};
    --green-dark: ${BrandColors.GREEN_DARK};
    --green-light: ${BrandColors.GREEN_LIGHT};
    --green-medium: ${BrandColors.GREEN_MEDIUM};
    --green-forest: ${BrandColors.GREEN_FOREST};

    --gray-700: ${BrandColors.GRAY_700};
    --gray-600: ${BrandColors.GRAY_600};
    --gray-500: ${BrandColors.GRAY_500};
    --gray-400: ${BrandColors.GRAY_400};
    --gray-300: ${BrandColors.GRAY_300};
    --gray-200: ${BrandColors.GRAY_200};

    --red: ${BrandColors.RED};
    --red-dark: ${BrandColors.RED_DARK};
    --red-bright: ${BrandColors.RED_BRIGHT};

    --navy: ${BrandColors.NAVY};
    --purple: ${BrandColors.PURPLE};
    --violet: ${BrandColors.VIOLET};
    --yellow: ${BrandColors.YELLOW};
    --yellow-amber: ${BrandColors.YELLOW_AMBER};

    --border-subtle: rgba(0, 0, 0, 0.1);

    /* RGB-triplet companions — for use with rgba() when alpha is needed. */
    --black-rgb: 0, 0, 0;
    --gray-600-rgb: 81, 90, 97;
    --gray-200-rgb: 213, 217, 220;
    --dark-700-rgb: 27, 32, 36;
    --text-secondary-rgb: 182, 181, 189;
  }
`;

export function AppLayout() {
  const { i18n, t } = useTranslation();
  const currentLangSelected = i18n.resolvedLanguage as SupportedLngsType;
  const pathname = useLocation().pathname;
  const isHome = pathname === "/";
  const [searchParams, setSearchParams] = useSearchParams();
  const matchIdFromUrl = searchParams.get("matchId");

  const { isLoading: isProfileLoading, isError: isProfileError } = useInit();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  useLocalizeDocumentAttributes();

  const persistedMatchId = getPersistedMatchId(TOURNAMENT_ID);
  const matchIdToUse = matchIdFromUrl || persistedMatchId || MATCH_ID;
  const {
    isLoading: isDefaultMatchLoading,
    data: defaultMatch,
    error: defaultMatchError,
    refetch,
  } = useDefaultMatch(TOURNAMENT_ID, matchIdToUse);

  const { hasMultipleLiveMatches } = useAppStore(
    useShallow(({ hasMultipleLiveMatches }) => ({ hasMultipleLiveMatches }))
  );
  const { match, setMatch } = useFixtureScore(
    useShallow(({ match, setMatch }) => ({ match, setMatch }))
  );

  useLayoutEffect(() => {
    if (defaultMatch && !Object.keys(match || {}).length) {
      setMatch(defaultMatch);
    }
  }, [defaultMatch, match, setMatch]);

  useEffect(() => {
    if (hasMultipleLiveMatches && match?.status === SoccerMatchStatus.PLAYING) {
      persistMatch(match, TOURNAMENT_ID);
    }
  }, [match, hasMultipleLiveMatches]);

  useEffect(() => {
    if (!currentLangSelected) return;
    void (async () => {
      await setDayjsLocale(currentLangSelected);
    })();
  }, [currentLangSelected]);

  const handleCarouselSelect = (m: { id: string }) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("matchId", m.id);
      // If the user was viewing tournament stats inline, drop back to the
      // match centre when they pick a match from the carousel.
      next.delete("view");
      return next;
    });
  };

  const isMatchCentre = pathname === "/match-centre";
  const needsMatch = isHome;
  const isMatchPending = needsMatch && isDefaultMatchLoading && !match?.id;
  const isMatchError = needsMatch && (defaultMatchError || (!isDefaultMatchLoading && !defaultMatch?.id));
  const isMatchCentreError = isMatchCentre && !matchIdFromUrl && !isDefaultMatchLoading && (!!defaultMatchError || !defaultMatch?.id);
  const isCarousel = pathname === "/carousel";
  const isBadUrlMatchId = Boolean(matchIdFromUrl) && isMatchError;

  return (
    <>
      <style>{rootStyles}</style>
      <main className={`app-main${isDesktop ? " app-main--desktop" : ""}`}>
        {isHome && <ScheduleCarousel onSelect={handleCarouselSelect} />}
        {!isCarousel && !isMatchCentre && (isProfileLoading || isMatchPending) ? (
          <DataLoader customStyle={{ height: "100%" }} />
        ) : isBadUrlMatchId ? (
          <div className="error-screen">
            <p className="error-text">
              We couldn&apos;t find that match. Visit the Fixtures page to see the latest schedule.
            </p>
          </div>
        ) : isProfileError || isMatchError || isMatchCentreError ? (
          <ErrorScreen
            errorText={t("errorState.error.somethingWentWrong")}
            refetch={refetch}
          />
        ) : (
          <Outlet />
        )}
      </main>
    </>
  );
}
